import { useEffect, useMemo, useState } from 'react';
import {
    MagnifyingGlass,
    X,
    Plus,
    Check,
    Barbell,
    Camera,
} from '@phosphor-icons/react';
import { api, formatApiErrorDetail } from '@/lib/api';
import { ScanMachine } from './ScanMachine';
import { EXERCISE_PICKER, SCAN } from '@/constants/testIds';

const CATEGORIES = [
    'all',
    'chest',
    'back',
    'legs',
    'shoulders',
    'arms',
    'core',
    'cardio',
];

const EQUIPMENT_OPTIONS = [
    'barbell',
    'dumbbell',
    'cable',
    'machine',
    'bodyweight',
    'other',
];

export function ExercisePicker({ open, onClose, onAdd }) {
    const [exercises, setExercises] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('all');
    const [selected, setSelected] = useState(new Set());
    const [error, setError] = useState('');

    // Custom-exercise mini form
    const [customOpen, setCustomOpen] = useState(false);
    const [cName, setCName] = useState('');
    const [cCategory, setCCategory] = useState('chest');
    const [cEquipment, setCEquipment] = useState('barbell');
    const [creating, setCreating] = useState(false);
    const [scanOpen, setScanOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const { data } = await api.get('/exercises');
                if (!cancelled) setExercises(data);
            } catch (e) {
                if (!cancelled)
                    setError(
                        formatApiErrorDetail(e.response?.data?.detail) ||
                            e.message
                    );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open]);

    // Reset selection when reopened
    useEffect(() => {
        if (open) {
            setSelected(new Set());
            setSearch('');
            setCategory('all');
            setCustomOpen(false);
        }
    }, [open]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return exercises.filter((e) => {
            if (category !== 'all' && e.category !== category) return false;
            if (q && !e.name.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [exercises, search, category]);

    function toggle(id) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    function commit() {
        const picks = exercises.filter((e) => selected.has(e.id));
        if (picks.length === 0) {
            onClose();
            return;
        }
        onAdd(picks);
        onClose();
    }

    async function createCustom() {
        if (!cName.trim()) return;
        setCreating(true);
        setError('');
        try {
            const { data } = await api.post('/exercises', {
                name: cName.trim(),
                category: cCategory,
                equipment: cEquipment,
                muscle_groups: [],
            });
            setExercises((prev) => [data, ...prev]);
            setSelected((prev) => new Set(prev).add(data.id));
            setCName('');
            setCustomOpen(false);
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
        } finally {
            setCreating(false);
        }
    }

    if (!open) return null;

    return (
        <div
            data-testid={EXERCISE_PICKER.root}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Sheet */}
            <div className="relative w-full sm:max-w-2xl max-h-[88vh] sm:max-h-[80vh] rounded-t-3xl sm:rounded-3xl glass-card grain flex flex-col overflow-hidden animate-fade-up">
                <div className="flex items-center justify-between gap-3 p-5 border-b border-foreground/10">
                    <div>
                        <p className="uppercase-mono text-muted-foreground">
                            Pick exercises
                        </p>
                        <h3 className="font-display font-extrabold tracking-tight text-2xl">
                            Catalog
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid={EXERCISE_PICKER.closeBtn}
                        className="grid place-items-center w-10 h-10 rounded-full border border-foreground/10 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.96] transition-all"
                        aria-label="Close picker"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </div>

                {/* Search + categories */}
                <div className="p-5 space-y-3 border-b border-foreground/10">
                    <div className="flex items-center gap-2 h-12 rounded-2xl border border-foreground/15 bg-foreground/5 px-4">
                        <MagnifyingGlass
                            size={18}
                            weight="bold"
                            className="text-muted-foreground shrink-0"
                        />
                        <input
                            data-testid={EXERCISE_PICKER.searchInput}
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Bench, squat, deadlift…"
                            className="flex-1 min-w-0 bg-transparent outline-none placeholder:text-muted-foreground/70"
                        />
                    </div>

                    <div
                        data-testid={EXERCISE_PICKER.categoryFilter}
                        className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1"
                    >
                        {CATEGORIES.map((c) => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => setCategory(c)}
                                data-testid={`exercise-picker-category-${c}`}
                                className={`shrink-0 uppercase-mono px-3 py-1.5 rounded-full border transition-colors ${
                                    category === c
                                        ? 'bg-foreground text-background border-foreground'
                                        : 'border-foreground/15 bg-foreground/5 hover:bg-foreground/10 text-muted-foreground'
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-5">
                    {error && (
                        <p className="text-sm text-[#FF4D4D] font-medium mb-3 px-2">
                            {error}
                        </p>
                    )}
                    {loading ? (
                        <div className="grid place-items-center py-12 uppercase-mono text-muted-foreground animate-soft-pulse">
                            Loading…
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-10 text-muted-foreground">
                            <p className="font-display text-lg">
                                No exercises match.
                            </p>
                            <p className="text-sm mt-1">
                                Try a different category or create a custom
                                exercise below.
                            </p>
                        </div>
                    ) : (
                        <ul className="grid sm:grid-cols-2 gap-2">
                            {filtered.map((ex) => {
                                const isSel = selected.has(ex.id);
                                return (
                                    <li key={ex.id}>
                                        <button
                                            type="button"
                                            onClick={() => toggle(ex.id)}
                                            data-testid={`${EXERCISE_PICKER.item}-${ex.id}`}
                                            className={`w-full text-left rounded-2xl border px-3 py-3 flex items-center gap-3 transition-all active:scale-[0.99] ${
                                                isSel
                                                    ? 'border-[#00FF9D]/60 bg-[#00FF9D]/10'
                                                    : 'border-foreground/10 bg-foreground/[0.03] hover:bg-foreground/[0.06]'
                                            }`}
                                        >
                                            <span
                                                className={`grid place-items-center w-9 h-9 rounded-xl shrink-0 ${
                                                    isSel
                                                        ? 'bg-[#00FF9D] text-[#121212]'
                                                        : 'bg-foreground/10 text-foreground'
                                                }`}
                                            >
                                                {isSel ? (
                                                    <Check
                                                        size={16}
                                                        weight="bold"
                                                    />
                                                ) : (
                                                    <Barbell
                                                        size={16}
                                                        weight="bold"
                                                    />
                                                )}
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block font-semibold tracking-tight truncate">
                                                    {ex.name}
                                                </span>
                                                <span className="uppercase-mono text-muted-foreground truncate">
                                                    {ex.category} ·{' '}
                                                    {ex.equipment}
                                                    {!ex.is_system &&
                                                        ' · custom'}
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {/* Footer — custom exercise + commit */}
                <div className="p-4 sm:p-5 border-t border-foreground/10 space-y-3">
                    {customOpen ? (
                        <div className="grid sm:grid-cols-[1.4fr_1fr_1fr_auto] gap-2">
                            <input
                                data-testid={EXERCISE_PICKER.customNameInput}
                                type="text"
                                value={cName}
                                onChange={(e) => setCName(e.target.value)}
                                placeholder="Exercise name"
                                className="h-11 rounded-2xl border border-foreground/15 bg-foreground/5 px-3 outline-none focus:border-foreground/40"
                            />
                            <select
                                data-testid={EXERCISE_PICKER.customCategorySelect}
                                value={cCategory}
                                onChange={(e) => setCCategory(e.target.value)}
                                className="h-11 rounded-2xl border border-foreground/15 bg-foreground/5 px-3 outline-none focus:border-foreground/40"
                            >
                                {CATEGORIES.filter((c) => c !== 'all').map(
                                    (c) => (
                                        <option key={c} value={c}>
                                            {c}
                                        </option>
                                    )
                                )}
                            </select>
                            <select
                                data-testid={EXERCISE_PICKER.customEquipmentSelect}
                                value={cEquipment}
                                onChange={(e) => setCEquipment(e.target.value)}
                                className="h-11 rounded-2xl border border-foreground/15 bg-foreground/5 px-3 outline-none focus:border-foreground/40"
                            >
                                {EQUIPMENT_OPTIONS.map((eq) => (
                                    <option key={eq} value={eq}>
                                        {eq}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={createCustom}
                                disabled={creating || !cName.trim()}
                                data-testid={EXERCISE_PICKER.createCustomBtn}
                                className="h-11 px-4 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight active:scale-[0.98] disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
                            >
                                <Plus size={16} weight="bold" /> Add
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => setCustomOpen(true)}
                                data-testid={EXERCISE_PICKER.createCustomToggle}
                                className="inline-flex items-center gap-2 h-11 px-4 rounded-2xl border border-dashed border-foreground/20 text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-all"
                            >
                                <Plus size={16} weight="bold" />
                                Create custom exercise
                            </button>
                            <button
                                type="button"
                                onClick={() => setScanOpen(true)}
                                data-testid={SCAN.triggerInPicker}
                                className="inline-flex items-center gap-2 h-11 px-4 rounded-2xl bg-[#00FF9D]/10 border border-[#00FF9D]/30 text-[#00FF9D] hover:bg-[#00FF9D]/15 active:scale-[0.98] transition-all"
                            >
                                <Camera size={16} weight="bold" />
                                Scan machine
                            </button>
                        </div>
                    )}

                    <div className="flex items-center justify-between gap-3">
                        <span className="uppercase-mono text-muted-foreground">
                            {selected.size} selected
                        </span>
                        <button
                            type="button"
                            onClick={commit}
                            disabled={selected.size === 0}
                            data-testid={EXERCISE_PICKER.addSelectedBtn}
                            className="h-12 px-6 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon hover:-translate-y-[2px] active:scale-[0.98] transition-all disabled:opacity-50 disabled:hover:translate-y-0 inline-flex items-center gap-2"
                        >
                            Add to workout
                            <Check size={16} weight="bold" />
                        </button>
                    </div>
                </div>
            </div>

            <ScanMachine
                open={scanOpen}
                onClose={() => setScanOpen(false)}
                onCommitted={(exercise) => {
                    setExercises((prev) => [exercise, ...prev]);
                    setSelected((prev) => new Set(prev).add(exercise.id));
                }}
            />
        </div>
    );
}
